import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/AuthContext';

export interface PricingTier {
  credits: number;
  price: number;
  tierName: string;
  badge?: string;
  isFeatured: boolean;
  displayOrder: number;
}

export interface PricingConfig {
  minCredits: number;
  maxCredits: number;
  creditStep: number;
  currency: string;
  creditConsumptionMode: 'per_call' | 'per_minute';
}

export interface PricingData {
  tiers: PricingTier[];
  config: PricingConfig;
  calculatedAt: string;
}

async function fetchPricingConfig(partnerId?: string): Promise<PricingData> {
  let url = 'get-pricing-config';
  if (partnerId) {
    url += `?partner_id=${encodeURIComponent(partnerId)}`;
  }
  
  const { data, error } = await supabase.functions.invoke(url, {
    method: 'GET',
  });

  if (error) {
    console.error('Error fetching pricing config:', error);
    throw new Error('Failed to fetch pricing configuration');
  }

  return data;
}

export function usePricingConfig() {
  const { profile } = useAuth();
  
  // Determine the right partner ID for pricing
  let partnerIdForPricing: string | undefined;
  
  if (profile?.user_type === 'partner_client' && profile?.partner_id) {
    // Sub-account: use parent partner's ID
    partnerIdForPricing = profile.partner_id;
  }
  // For regular users or partners themselves, don't pass partner_id (use default pricing)
  // Partners manage their own custom pricing, but their personal accounts use default pricing

  return useQuery({
    queryKey: ['pricing-config', partnerIdForPricing],
    queryFn: () => fetchPricingConfig(partnerIdForPricing),
    staleTime: 0, // No caching - always fetch fresh data
    retry: 2,
    retryDelay: 1000,
    enabled: !!profile, // Wait for profile to load
  });
}

// Helper function to calculate price for custom credit amounts
export function calculatePriceFromTiers(credits: number, tiers: PricingTier[]): number {
  if (!tiers || tiers.length === 0) {
    return credits * 0.60; // Fallback rate
  }

  // Check for per-credit rate model (tier with 1 credit = per-credit rate)
  const perCreditRateTier = tiers.find(tier => tier.credits === 1 && tier.tierName === 'per_credit_rate');
  if (perCreditRateTier) {
    // Simple per-credit calculation
    return credits * perCreditRateTier.price;
  }

  // Legacy tier-based system (complex interpolation)
  const sortedTiers = [...tiers].sort((a, b) => a.credits - b.credits);

  // Check for exact match
  const exactMatch = sortedTiers.find(tier => tier.credits === credits);
  if (exactMatch) {
    return exactMatch.price;
  }

  // Find tiers for interpolation
  const lowerTier = [...sortedTiers].reverse().find(tier => tier.credits < credits);
  const upperTier = sortedTiers.find(tier => tier.credits > credits);

  if (!lowerTier && !upperTier) {
    // No tiers available, fallback
    return credits * 0.60;
  } else if (!lowerTier) {
    // Below smallest tier, use linear extrapolation
    return (credits / upperTier!.credits) * upperTier!.price;
  } else if (!upperTier) {
    // Above largest tier, lock in the best rate from the highest tier
    const highestTier = sortedTiers[sortedTiers.length - 1];
    const bestRate = highestTier.price / highestTier.credits;
    return credits * bestRate;
  } else {
    // Interpolate between two tiers
    const ratio = (credits - lowerTier.credits) / (upperTier.credits - lowerTier.credits);
    return lowerTier.price + ratio * (upperTier.price - lowerTier.price);
  }
} 